import { Suspense } from "react";
import { Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { useActivities } from "../hooks/use-activities";
import { ActivityCardDiscover } from "./activity-card-discover";

import { Icon } from "@majourneys/icons";
interface FallbackSuggestionsProps {
  /** The words searched for, when the empty list is a search that found nothing. */
  query?: string;
  lang: string;
  categorySlug?: string;
  citySlug?: string;
  categoryName?: string;
  cityName?: string;
}

export function FallbackSuggestions(props: FallbackSuggestionsProps) {
  return (
    <Suspense fallback={null}>
      <FallbackInner {...props} />
    </Suspense>
  );
}

function FallbackInner({
  query,
  lang,
  categorySlug,
  citySlug,
  categoryName,
  cityName,
}: FallbackSuggestionsProps) {
  const { t } = useTranslation();
  const hasBoth = Boolean(categorySlug && citySlug);

  return (
    <div className="space-y-12 py-4">
      <div className="rounded-2xl border border-white/70 bg-white/70 p-8 text-center shadow-[0_6px_18px_rgba(4,0,51,0.05)] backdrop-blur-xl">
        <Icon name="ri-compass-3-line" className="text-[40px] text-[#5a5d77]" aria-hidden="true" />
        <h2 className="mt-3 font-sans text-[22px] font-semibold text-[#110E39]">
          {query
            ? t("nav.search_no_results", { query })
            : hasBoth && categoryName && cityName
            ? t("activity.empty_combo_title", {
                category: categoryName,
                city: cityName,
              })
            : t("activity.no_activities", { defaultValue: "No activities found" })}
        </h2>
        <p className="mx-auto mt-2 max-w-xl text-[14px] text-[#4b4978]">
          {t("activity.empty_combo_body", {
            defaultValue:
              "We're adding more experiences every week. In the meantime, explore nearby alternatives below.",
          })}
        </p>
        <div className="mt-5 flex flex-wrap items-center justify-center gap-3">
          {citySlug && (
            <Link
              to="/$lang/place/$city"
              params={{ lang, city: citySlug }}
              className="inline-flex h-10 items-center rounded-full bg-[color:var(--color-primary)] px-5 text-[13px] font-semibold text-white shadow-[0_6px_18px_rgba(4,0,51,0.22)] transition hover:opacity-90"
            >
              {t("activity.see_all_in_city", {
                defaultValue: `All things to do in ${cityName ?? ""}`,
                city: cityName ?? "",
              })}
            </Link>
          )}
          {categorySlug && (
            <Link
              to="/$lang/category/$category"
              params={{ lang, category: categorySlug }}
              className="inline-flex h-10 items-center rounded-full border border-white/70 bg-white/70 px-5 text-[13px] font-semibold text-[#110E39] backdrop-blur-xl transition hover:bg-white"
            >
              {t("activity.see_category_everywhere", {
                defaultValue: `${categoryName ?? "Category"} in other cities`,
                category: categoryName ?? "",
              })}
            </Link>
          )}
          <Link
            to="/$lang/explore"
            params={{ lang }}
            className="inline-flex h-10 items-center rounded-full border border-white/70 bg-white/70 px-5 text-[13px] font-semibold text-[#110E39] backdrop-blur-xl transition hover:bg-white"
          >
            {t("activity.browse_all", { defaultValue: "Browse all" })}
          </Link>
        </div>
      </div>

      {citySlug && (
        <Suggestion
          title={t("activity.other_in_city", {
            defaultValue: `Other things to do in ${cityName ?? "this city"}`,
            city: cityName ?? "",
          })}
          filters={{ city: citySlug, page_size: "8", page: "1" }}
          excludeCategory={categorySlug}
        />
      )}

      {categorySlug && (
        <Suggestion
          title={t("activity.category_elsewhere", {
            defaultValue: `${categoryName ?? "Activities"} in other cities`,
            category: categoryName ?? "",
          })}
          filters={{ category: categorySlug, page_size: "8", page: "1" }}
          excludeCity={citySlug}
        />
      )}
    </div>
  );
}

interface SuggestionProps {
  title: string;
  filters: { category?: string; city?: string; page_size?: string; page?: string };
  excludeCategory?: string;
  excludeCity?: string;
}

function Suggestion({ title, filters, excludeCategory, excludeCity }: SuggestionProps) {
  const { data: response } = useActivities(filters);
  const items = (response.data as Array<{
    id: string;
    slug: string;
    title: string;
    images: string[];
    price: number;
    typePrice?: number;
    rating?: number;
    isNew?: boolean;
    category: { id: string; name: string; slug: string };
    city: { id: string; name: string; slug: string };
  }>).filter((a) => {
    if (excludeCategory && a.category.slug === excludeCategory) return false;
    if (excludeCity && a.city.slug === excludeCity) return false;
    return true;
  });

  if (items.length === 0) return null;

  return (
    <section>
      <h3 className="mb-4 font-sans text-[18px] font-semibold text-[#110E39]">{title}</h3>
      <div className="grid grid-cols-2 gap-x-2.5 gap-y-5 min-[400px]:gap-x-3 sm:gap-5 lg:grid-cols-3 xl:grid-cols-4">
        {items.slice(0, 8).map((a) => (
          <ActivityCardDiscover
            key={a.id}
            slug={a.slug}
            images={a.images}
            title={a.title}
            city={a.city.name}
            citySlug={a.city.slug}
            category={a.category.name}
            categorySlug={a.category.slug}
            segment={(a as { segment?: string | null }).segment ?? null}
            tags={(a as { tags?: readonly string[] | null }).tags ?? null}
            mode={(a as { mode?: string }).mode}
            price={a.price}
            isNew={a.isNew === true}
            typePrice={a.typePrice}
            rating={a.rating}
          />
        ))}
      </div>
    </section>
  );
}
